import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import type { AppDispatch } from "@/app/store";
import { thunkInitializeAuth } from "@/modules/auth/store/authThunks";
import { useTwoFactorAction, type UseTwoFactorActionResult } from "@/shared/hooks/useTwoFactorAction";

import { PayoutBatchesService } from "../services/payoutBatches.service";
import type { PayoutBatchApprovalPolicy } from "../types/payoutBatches.types";

export interface UsePayoutBatchApprovalPolicyResult {
  policy: PayoutBatchApprovalPolicy | null;
  isLoading: boolean;
  hasError: boolean;
  reload: () => void;
  /**
   * Flips the organization's switch behind the step-up prompt. Resolves with the new policy, or `null` when
   * the prompt was closed; any other failure rejects.
   */
  setSelfApproval: (enabled: boolean) => Promise<PayoutBatchApprovalPolicy | null>;
  twoFactor: UseTwoFactorActionResult<PayoutBatchApprovalPolicy>;
}

interface PolicyState {
  organizationId: string;
  policy: PayoutBatchApprovalPolicy;
}

/**
 * Whether the caller may approve the batches they prepare, read when an organization is given (null reads
 * nothing). A change re-reads `/auth/me` too, whose organization carries the same switch.
 */
export function usePayoutBatchApprovalPolicy(
  organizationId: string | null,
): UsePayoutBatchApprovalPolicyResult {
  const dispatch = useDispatch<AppDispatch>();
  const twoFactor = useTwoFactorAction<PayoutBatchApprovalPolicy>();
  const { start } = twoFactor;
  const [state, setState] = useState<PolicyState | null>(null);
  const [settledFor, setSettledFor] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const requestSeq = useRef(0);

  const read = useCallback(async () => {
    if (!organizationId) return;
    const seq = ++requestSeq.current;
    try {
      const { data } = await PayoutBatchesService.getApprovalPolicy(organizationId);
      if (seq !== requestSeq.current) return;
      setState({ organizationId, policy: data });
      setHasError(false);
      setSettledFor(organizationId);
    } catch {
      if (seq !== requestSeq.current) return;
      setHasError(true);
      setSettledFor(organizationId);
    }
  }, [organizationId]);

  const readRef = useRef(read);
  useEffect(() => {
    readRef.current = read;
  });

  // Through the ref on purpose: the read settles asynchronously, so no render cascades from here.
  useEffect(() => {
    void readRef.current();
  }, [organizationId]);

  const reload = useCallback(() => {
    setSettledFor(null);
    void read();
  }, [read]);

  const setSelfApproval = useCallback(
    async (enabled: boolean) => {
      if (!organizationId) return null;
      const next = await start(async (code) => {
        const { data } = await PayoutBatchesService.updateApprovalPolicy(organizationId, enabled, code);
        return data;
      });
      if (!next) return null;
      // A read still in flight predates the change.
      requestSeq.current += 1;
      setState({ organizationId, policy: next });
      setHasError(false);
      setSettledFor(organizationId);
      await (dispatch(thunkInitializeAuth()) as unknown as Promise<void>);
      return next;
    },
    [dispatch, organizationId, start],
  );

  return {
    policy: state?.organizationId === organizationId ? state.policy : null,
    isLoading: organizationId !== null && settledFor !== organizationId,
    hasError,
    reload,
    setSelfApproval,
    twoFactor,
  };
}
